import { Controller } from "@hotwired/stimulus"
import debounce from "lodash/debounce"
import { get } from "@rails/request.js"

export default class extends Controller {
  static targets = [
    "existingCustomer",
    "manualCustomer",
    "tableSelect",
    "customerSearch",
    "customerSearchResults",
    "selectedCustomer",
    "userId",
    "customerName",
    "customerEmail",
    "customerPhone",
    "dateField",
    "timeField",
    "partySizeField"
  ]

  static values = {
    searchUrl: String,
    availableTablesUrl: String
  }

  connect() {
    this.searchCustomer = debounce(this.searchCustomer.bind(this), 300)

    // If a user is already selected (e.g., after form error), keep that state
    if (this.hasUserIdTarget && this.userIdTarget.value) {
      return
    }

    this.showManualCustomer()
  }

  toggleCustomerType(event) {
    if (event.target.checked) {
      this.showExistingCustomer()
    } else {
      this.showManualCustomer()
    }
  }

  showExistingCustomer() {
    this.existingCustomerTarget.style.display = "block"
    this.manualCustomerTarget.style.display = "none"

    // Clear manual fields and make them not required
    const manualInputs = this.manualCustomerTarget.querySelectorAll("input")
    manualInputs.forEach(input => {
      input.value = ""
      input.required = false
    })

    // Make search input required since no customer selected yet
    this.searchInput.required = true
  }

  showManualCustomer() {
    this.existingCustomerTarget.style.display = "none"
    this.manualCustomerTarget.style.display = "block"

    // Clear existing customer selection
    this.clearCustomerSelection()

    // Search input not required since section is hidden
    this.searchInput.required = false

    // Make manual fields required
    const nameInput = this.manualCustomerTarget.querySelector("[name*='customer_name']")
    const emailInput = this.manualCustomerTarget.querySelector("[name*='customer_email']")

    if (nameInput) nameInput.required = true
    if (emailInput) emailInput.required = true
  }

  searchCustomer(event) {
    const query = event.target.value

    if (query.length < 2) {
      this.clearSearchResults()
      return
    }

    get(`${this.searchUrlValue}?query=${encodeURIComponent(query)}`, {
      responseKind: "turbo-stream"
    })
  }

  selectCustomer(event) {
    const { userId, userName, userEmail } = event.currentTarget.dataset

    // Set the hidden user_id field
    this.userIdTarget.value = userId

    // Show selected customer info
    this.customerSearchTarget.style.display = "none"
    this.selectedCustomerTarget.style.display = "flex"
    this.selectedCustomerTarget.querySelector("[data-selected-name]").textContent = userName || "No name"
    this.selectedCustomerTarget.querySelector("[data-selected-email]").textContent = userEmail

    // Search input no longer required since customer is selected
    this.searchInput.required = false

    // Clear search results
    this.clearSearchResults()
  }

  clearCustomerSelection() {
    if (this.hasUserIdTarget) {
      this.userIdTarget.value = ""
    }
    if (this.hasCustomerSearchTarget) {
      this.customerSearchTarget.style.display = "block"
      this.searchInput.value = ""
      this.searchInput.required = true
    }
    if (this.hasSelectedCustomerTarget) {
      this.selectedCustomerTarget.style.display = "none"
    }
    this.clearSearchResults()
  }

  clearSearchResults() {
    const resultsContainer = document.getElementById("customer_search_results")
    if (resultsContainer) {
      resultsContainer.innerHTML = ""
    }
  }

  dateChanged(event) {
    this.refreshAvailableTables()
  }

  timeChanged(event) {
    this.refreshAvailableTables()
  }

  partySizeChanged(event) {
    this.refreshAvailableTables()
  }

  refreshAvailableTables() {
    if (!this.availableTablesUrlValue) return

    const date = this.dateFieldTarget.value
    const time = this.timeFieldTarget.value
    const partySize = this.partySizeFieldTarget.value

    if (!date || !time || !partySize) return

    const url = new URL(this.availableTablesUrlValue, window.location.origin)
    url.searchParams.set("date", date)
    url.searchParams.set("time", time)
    url.searchParams.set("party_size", partySize)

    // Preserve currently selected table
    if (this.hasTableSelectTarget && this.tableSelectTarget.value) {
      url.searchParams.set("selected_table_id", this.tableSelectTarget.value)
    }

    get(url.toString(), { responseKind: "turbo-stream" })
  }

  get searchInput() {
    return this.customerSearchTarget.querySelector("input")
  }
}
